/**
 * Anomaly List (#579).
 *
 * Lists persisted anomaly events for the current tenant with severity /
 * resolution-state filters. Admins can mark a row resolved with a
 * short note. Tenant scoping is enforced server-side from the JWT.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ActivityErrorBanner from '@components/activity/ActivityErrorBanner';
import AnomalyFilters from '@components/activity/anomalies/AnomalyFilters';
import AnomalyTable from '@components/activity/anomalies/AnomalyTable';
import ResolveAnomalyDialog from '@components/activity/anomalies/ResolveAnomalyDialog';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import {
  AnomalyRow,
  AnomalySeverity,
  activityService,
} from '@services/activity.service';

const AnomalyListPage: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<AnomalyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [severity, setSeverity] = useState<'' | AnomalySeverity>('');
  const [resolved, setResolved] = useState<'' | 'true' | 'false'>('false');
  const [dialogTarget, setDialogTarget] = useState<AnomalyRow | null>(null);
  const [dialogNote, setDialogNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await activityService.listAnomalies({
        severity: severity || undefined,
        resolved: resolved === '' ? undefined : resolved === 'true',
        limit: 100,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(t('anomalies.fetchFailed', { message: resolveApiErrorMessage(t, e) }));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds the fetch-error message; excluded so a language change doesn't refetch the anomaly list (PR #790)
  }, [severity, resolved]);

  useEffect(() => { load(); }, [load]);

  const onResolve = useCallback(async () => {
    if (!dialogTarget) return;
    setResolving(true);
    try {
      await activityService.resolveAnomaly(dialogTarget.id, dialogNote || undefined);
      setDialogTarget(null);
      setDialogNote('');
      await load();
    } catch (e) {
      setError(t('anomalies.resolveFailed', { message: resolveApiErrorMessage(t, e) }));
    } finally {
      setResolving(false);
    }
  }, [dialogTarget, dialogNote, load, t]);

  return (
    <div className="p-6">
      <h4 className="revy-cjk mb-2 text-2xl font-semibold leading-[1.4] tracking-tight">
        {t('anomalies.title')}
      </h4>

      <AnomalyFilters
        severity={severity}
        resolved={resolved}
        total={total}
        onSeverityChange={setSeverity}
        onResolvedChange={setResolved}
      />

      {error && <ActivityErrorBanner message={error} />}

      <AnomalyTable items={items} loading={loading} onResolve={setDialogTarget} />

      <ResolveAnomalyDialog
        target={dialogTarget}
        note={dialogNote}
        resolving={resolving}
        onNoteChange={setDialogNote}
        onClose={() => setDialogTarget(null)}
        onConfirm={onResolve}
      />
    </div>
  );
};

export default AnomalyListPage;
